/**
 * Recipe editor controller
 *
 * Owns every piece of state RecipeEditorModal needs (menu-item/inventory-item
 * pagination, units, the save mutation) plus the open/close lifecycle —
 * extracted out of InventoryRecipesPage so any page can mount the same
 * modal. Lifted verbatim from InventoryRecipesPage's previous local state;
 * behavior for that page is unchanged. openCreateModalForMenuItem is the
 * only new entry point, used by the Missing Recipes page's row action.
 */

import type {
  InventoryItemRecord,
  InventoryRecipeRecord,
  InventoryUnitRecord,
} from '@/renderer/types/inventory';
import type { MenuItem } from '@/renderer/types/menu';
import {
  useInventoryItems,
  useInventoryUnits,
  useSaveRecipe,
} from '@hooks/inventory';
import { useMenuItemsPaginated } from '@hooks/menu';
import { useEffect, useState } from 'react';
import {
  buildSaveRecipePayload,
  createEmptyRecipeForm,
  createRecipeFormFromRecord,
  RecipeFormState,
} from './recipeForm';

interface PrefillMenuItem {
  id: string;
  name: string;
}

export function useRecipeEditorController(
  companyId: number | null,
  effectiveOutletId: string | null,
) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [menuItemSearch, setMenuItemSearch] = useState('');
  const [menuItemPage, setMenuItemPage] = useState(1);
  const [loadedMenuItems, setLoadedMenuItems] = useState<MenuItem[]>([]);
  const [inventoryItemSearch, setInventoryItemSearch] = useState('');
  const [inventoryItemPage, setInventoryItemPage] = useState(1);
  const [loadedInventoryItems, setLoadedInventoryItems] = useState<
    InventoryItemRecord[]
  >([]);
  const [editingRecipe, setEditingRecipe] =
    useState<InventoryRecipeRecord | null>(null);
  const [prefillMenuItem, setPrefillMenuItem] =
    useState<PrefillMenuItem | null>(null);

  const menuItemsQuery = useMenuItemsPaginated({
    outletId: effectiveOutletId ?? '',
    page: menuItemPage,
    pageSize: 30,
    search: menuItemSearch.trim() || undefined,
    status: 'active',
  });
  const itemsQuery = useInventoryItems({
    companyId: companyId ?? 0,
    page: inventoryItemPage,
    pageSize: 30,
    search: inventoryItemSearch.trim() || undefined,
    status: 'active',
  });
  const unitsQuery = useInventoryUnits(companyId);

  const menuItems = loadedMenuItems;
  const inventoryItems = loadedInventoryItems;
  const units: InventoryUnitRecord[] = unitsQuery.data ?? [];

  useEffect(() => {
    setMenuItemPage(1);
    setLoadedMenuItems([]);
  }, [menuItemSearch, effectiveOutletId]);

  useEffect(() => {
    setInventoryItemPage(1);
    setLoadedInventoryItems([]);
  }, [inventoryItemSearch]);

  useEffect(() => {
    if (menuItemsQuery.data?.pagination.page !== menuItemPage) {
      return;
    }
    const nextItems = menuItemsQuery.data?.data ?? [];
    setLoadedMenuItems((currentItems) => {
      const mergedItems =
        menuItemPage === 1 ? nextItems : [...currentItems, ...nextItems];
      return Array.from(
        new Map(mergedItems.map((item) => [item.id, item])).values(),
      );
    });
  }, [menuItemPage, menuItemsQuery.data]);

  useEffect(() => {
    if (itemsQuery.data?.pagination.page !== inventoryItemPage) {
      return;
    }
    const nextItems = itemsQuery.data?.data ?? [];
    setLoadedInventoryItems((currentItems) => {
      const mergedItems =
        inventoryItemPage === 1 ? nextItems : [...currentItems, ...nextItems];
      return Array.from(
        new Map(mergedItems.map((item) => [item.id, item])).values(),
      );
    });
  }, [inventoryItemPage, itemsQuery.data]);

  const { mutateAsync: saveRecipeAsync, isPending: isSaving } = useSaveRecipe();

  const resetSearchState = () => {
    setMenuItemSearch('');
    setMenuItemPage(1);
    setInventoryItemSearch('');
    setInventoryItemPage(1);
  };

  const openCreateModal = () => {
    setEditingRecipe(null);
    setPrefillMenuItem(null);
    resetSearchState();
    setIsModalOpen(true);
  };

  const openEditModal = (recipe: InventoryRecipeRecord) => {
    setEditingRecipe(recipe);
    setPrefillMenuItem(null);
    resetSearchState();
    setIsModalOpen(true);
  };

  const openCreateModalForMenuItem = (menuItem: PrefillMenuItem) => {
    setEditingRecipe(null);
    setPrefillMenuItem(menuItem);
    resetSearchState();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isSaving) return;
    setIsModalOpen(false);
    setEditingRecipe(null);
    setPrefillMenuItem(null);
  };

  const handleSubmit = async (
    payload: ReturnType<typeof buildSaveRecipePayload>,
  ) => {
    if (!companyId) return;
    await saveRecipeAsync(payload);
    setIsModalOpen(false);
    setEditingRecipe(null);
    setPrefillMenuItem(null);
  };

  const initial: RecipeFormState = editingRecipe
    ? createRecipeFormFromRecord(editingRecipe)
    : {
        ...createEmptyRecipeForm(),
        outletId: effectiveOutletId ?? '',
        ...(prefillMenuItem
          ? {
              menuItemId: prefillMenuItem.id,
              menuItemLabel: prefillMenuItem.name,
              name: prefillMenuItem.name,
            }
          : {}),
      };

  const modalProps =
    companyId != null
      ? {
          companyId,
          existingId: editingRecipe?.id,
          initial,
          isSaving,
          title: editingRecipe ? 'Edit recipe' : 'Create recipe',
          menuItems,
          menuItemsHasMore:
            menuItemsQuery.data?.pagination.hasNextPage ?? false,
          menuItemsLoading: menuItemsQuery.isFetching,
          onMenuItemsSearchChange: setMenuItemSearch,
          onLoadMoreMenuItems: () =>
            setMenuItemPage((currentPage) => currentPage + 1),
          inventoryItems,
          inventoryItemsHasMore:
            itemsQuery.data?.pagination.hasNextPage ?? false,
          inventoryItemsLoading: itemsQuery.isFetching,
          onInventoryItemsSearchChange: setInventoryItemSearch,
          onLoadMoreInventoryItems: () =>
            setInventoryItemPage((currentPage) => currentPage + 1),
          units,
          onClose: closeModal,
          onSubmit: handleSubmit,
        }
      : null;

  return {
    isModalOpen,
    openCreateModal,
    openEditModal,
    openCreateModalForMenuItem,
    modalProps,
  };
}
